import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { boardAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { ArrowLeft, Download, Zap, Brain, Shield, BarChart3, Target, TrendingUp, Trash2 } from "lucide-react";

const PIE_COLORS = { delete: "#EF4444", automate: "#F59E0B", augment: "#8B5CF6", protect: "#10B981", undecided: "#94A3B8" };

export default function AnalyticsPage() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, boardRes] = await Promise.all([
        boardAPI.analytics(boardId),
        boardAPI.get(boardId),
      ]);
      setAnalytics(analyticsRes.data);
      setBoard(boardRes.data);
    } catch {
      toast.error("Failed to load analytics");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [boardId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportBoard = async (format = "json") => {
    try {
      const res = await boardAPI.exportBoard(boardId, format);
      const blob = new Blob(
        [format === "csv" ? res.data.data : JSON.stringify(res.data, null, 2)],
        { type: format === "csv" ? "text/csv" : "application/json" }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${board?.name || "board"}-export.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.data.count} use cases as ${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-pulse font-heading text-lg text-slate-500">Loading analytics...</div>
      </div>
    );
  }

  if (!analytics || !board) return null;

  const pieData = [
    { name: "Delete", value: analytics.delete_count, color: PIE_COLORS.delete },
    { name: "Automate", value: analytics.automate_count, color: PIE_COLORS.automate },
    { name: "Augment", value: analytics.augment_count, color: PIE_COLORS.augment },
    { name: "Protect", value: analytics.protect_count, color: PIE_COLORS.protect },
    { name: "Undecided", value: analytics.undecided_count, color: PIE_COLORS.undecided },
  ].filter((d) => d.value > 0);

  const barData = analytics.department_stats.map((d) => ({
    name: d.name.length > 12 ? d.name.slice(0, 12) + "..." : d.name,
    fullName: d.name,
    Delete: d.delete || 0,
    Automate: d.automate,
    Augment: d.augment,
    Protect: d.protect,
    Undecided: d.undecided,
  }));

  const quickWins = [];
  board.departments.forEach((dept) => {
    if (!dept.is_applicable) return;
    dept.stalls.forEach((stall) => {
      if (stall.decision === "automate" && stall.confidence >= 4) {
        quickWins.push({ ...stall, department: dept.name });
      }
    });
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="analytics-page">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              data-testid="back-to-board-btn"
              onClick={() => navigate(`/board/${boardId}`)}
              className="h-8 px-2 text-slate-500"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-base text-slate-900">{board.name}</h1>
              <span className="text-xs text-slate-400">Board Analytics</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="export-json-btn"
              onClick={() => exportBoard("json")}
              className="text-xs"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export JSON
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="export-csv-btn"
              onClick={() => exportBoard("csv")}
              className="text-xs"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            {
              label: "Progress",
              value: `${analytics.progress}%`,
              sub: `${analytics.decided_stalls}/${analytics.total_stalls} stalls`,
              icon: Target,
              color: "text-indigo-600 bg-indigo-50",
            },
            {
              label: "Delete",
              value: analytics.delete_count || 0,
              sub: "Workflows",
              icon: Trash2,
              color: "text-red-600 bg-red-50",
            },
            {
              label: "Automate",
              value: analytics.automate_count,
              sub: "Workflows",
              icon: Zap,
              color: "text-amber-600 bg-amber-50",
            },
            {
              label: "Augment",
              value: analytics.augment_count,
              sub: "Workflows",
              icon: Brain,
              color: "text-violet-600 bg-violet-50",
            },
            {
              label: "Protect",
              value: analytics.protect_count,
              sub: "Workflows",
              icon: Shield,
              color: "text-emerald-600 bg-emerald-50",
            },
          ].map((stat, i) => (
            <div
              key={i}
              data-testid={`stat-card-${i}`}
              className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm animate-fade-in"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{stat.label}</span>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-4 h-4" />
                </div>
              </div>
              <div className="font-heading font-bold text-2xl text-slate-900">{stat.value}</div>
              <div className="text-xs text-slate-400 mt-0.5">{stat.sub}</div>
            </div>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-8">
          <div className="flex items-center justify-between mb-3">
            <span className="font-heading font-semibold text-sm text-slate-900">Overall Completion</span>
            <span className="font-mono text-sm text-slate-500">{analytics.progress}%</span>
          </div>
          <Progress value={analytics.progress} className="h-3" />
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
            <span>Avg Confidence: {analytics.avg_confidence}/5</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Decision Distribution Pie */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-heading font-semibold text-base text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-500" />
              Decision Distribution
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Department Breakdown Bar */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-heading font-semibold text-base text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              Department Breakdown
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                <RTooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  formatter={(value, name) => [value, name]}
                />
                <Bar dataKey="Delete" stackId="a" fill="#EF4444" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Automate" stackId="a" fill="#F59E0B" />
                <Bar dataKey="Augment" stackId="a" fill="#8B5CF6" />
                <Bar dataKey="Protect" stackId="a" fill="#10B981" />
                <Bar dataKey="Undecided" stackId="a" fill="#CBD5E1" radius={[0, 4, 4, 0]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Wins */}
        {quickWins.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-8">
            <h3 className="font-heading font-semibold text-base text-slate-900 mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              Quick Wins — High-Confidence Automation Opportunities
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {quickWins.map((stall, i) => (
                <div key={i} className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                  <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200 mb-2">
                    {stall.department}
                  </Badge>
                  <h4 className="font-heading font-semibold text-sm text-amber-900">{stall.name}</h4>
                  <p className="text-xs text-amber-700 opacity-70 mt-1 line-clamp-2">{stall.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Heatmap */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-heading font-semibold text-base text-slate-900 mb-4">
            Board Heatmap
          </h3>
          <div className="overflow-x-auto scrollbar-thin">
            <div className="flex gap-1 min-w-max">
              {analytics.department_stats.map((dept, di) => (
                <div key={di} className="w-16">
                  <div className="text-[9px] text-slate-500 font-medium truncate text-center mb-1" title={dept.name}>
                    {dept.name.slice(0, 8)}
                  </div>
                  <div className="space-y-1">
                    {board.departments[di]?.stalls.map((stall, si) => {
                      const colors = {
                        delete: "bg-red-400",
                        automate: "bg-amber-400",
                        augment: "bg-violet-400",
                        protect: "bg-emerald-400",
                      };
                      return (
                        <div
                          key={si}
                          className={`w-full h-5 rounded-sm ${
                            stall.decision ? colors[stall.decision] : "bg-slate-200"
                          }`}
                          title={`${stall.name}: ${stall.decision || "undecided"}`}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-400" /> Delete</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400" /> Automate</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-violet-400" /> Augment</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-400" /> Protect</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-200" /> Undecided</span>
          </div>
        </div>
      </main>
    </div>
  );
}
